"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { track } from "@/shared/lib/analytics"
import { getPostHogFeatureFlagClient } from "@/shared/lib/monitoring/telemetry-config"
import {
  CRO_CHECKOUT_REASSURANCE_FLAG,
  type CheckoutReassuranceVariant,
  type PostHogWithReassuranceFlags,
  normalizeCheckoutReassuranceVariant,
} from "@/features/credits/lib/checkout-reassurance-experiment"

function getPostHog(): PostHogWithReassuranceFlags | null {
  return getPostHogFeatureFlagClient<PostHogWithReassuranceFlags>()
}

export function useCheckoutReassuranceExperiment(): {
  variant: CheckoutReassuranceVariant
  showReassurance: boolean
  eventProps: Partial<Record<string, string>>
} {
  const [variant, setVariant] = useState<CheckoutReassuranceVariant>("unassigned")
  const exposedRef = useRef(false)

  useEffect(() => {
    const ph = getPostHog()
    if (!ph) return

    const apply = () => {
      const resolved = normalizeCheckoutReassuranceVariant(
        ph.getFeatureFlag(CRO_CHECKOUT_REASSURANCE_FLAG)
      )
      setVariant(resolved)

      if (!exposedRef.current && resolved !== "unassigned") {
        exposedRef.current = true
        track("sub:checkout_reassurance_exposure", {
          flag: CRO_CHECKOUT_REASSURANCE_FLAG,
          variant: resolved,
        })
      }
    }

    apply()
    const unsubscribe = ph.onFeatureFlags?.(apply)
    return typeof unsubscribe === "function" ? unsubscribe : undefined
  }, [])

  const showReassurance = variant === "reassurance_guarantee"

  const eventProps = useMemo(
    () =>
      variant !== "unassigned"
        ? {
            checkout_reassurance_flag: CRO_CHECKOUT_REASSURANCE_FLAG,
            checkout_reassurance_variant: variant,
          }
        : {},
    [variant]
  )

  return { variant, showReassurance, eventProps }
}
